import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'

import { supportedLanguages } from '~/data/lang/settings'
import { sanityClientCdn } from '~/services/sanity'
import { addTranslations } from '~/utils/i18n'
import type { PageType } from '~/utils/urlResolver'
import { toURL, urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import type { ArtworkParams, PDPPageData } from '../../../artwork/[artworkSlug]'
import PDP, { pdpGetStaticProps } from '../../../artwork/[artworkSlug]'

export const CollectionPDP: NextPage<PDPPageData> = (props: PDPPageData) => {
  return <PDP {...props} />
}

// Wrapper that calls pdpGetStaticProps with localizedData
const getStaticPropsOriginal = async (
  context: GetStaticPropsContext<ArtworkParams>,
  localizedData: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<PDPPageData>> => {
  return pdpGetStaticProps(context, localizedData)
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    // Type assertion to match the expected params structure
    const typedContext = context as GetStaticPropsContext<ArtworkParams>
    const result = await getStaticPropsOriginal(typedContext, localizedData)

    // Check if it's a redirect or notFound result
    if ('redirect' in result || 'notFound' in result) {
      // If it's trying to redirect to artist page, redirect to collection instead
      if ('redirect' in result && context.params?.collection) {
        // During static build, return notFound to avoid Next.js build errors
        // During runtime (SSR/ISR), return redirect for better UX
        const isStaticBuild =
          process.env.NODE_ENV === 'production' && !process.env.VERCEL_URL

        if (isStaticBuild) {
          return { notFound: true }
        }

        return {
          redirect: {
            permanent: true,
            destination: toURL({
              _type: 'collection',
              slug: Array.isArray(context.params.collection)
                ? context.params.collection[0]
                : context.params.collection,
              collectionTaxonomy: Array.isArray(
                context.params.collectionTaxonomy
              )
                ? context.params.collectionTaxonomy[0]
                : context.params.collectionTaxonomy,
              artist: Array.isArray(context.params.slug)
                ? context.params.slug[0]
                : context.params.slug,
            }),
          },
        }
      }

      return result
    }

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => {
  const limiter =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'main'
      ? '[0..10]'
      : '[0..10]'

  const pages = await sanityClientCdn.fetch<Array<{ url: PageType }>>(
    `*[(_type in $pageTypes && _type != 'artist' && defined(slug.current) && (!defined(isVisible) || isVisible == true) && !(_id in path('drafts.**')))]
      | score(
        boost(_type match "artwork", 2))
      | order(_score desc)
        ${limiter} {
        ${urlQuery}
      }`,
    {
      pageTypes: ['artwork'],
    }
  )

  const paths = (pages?.length ? pages : [])
    .filter(p => {
      return (
        p.url.slug &&
        p.url.artist &&
        p.url.collection &&
        p.url.collectionTaxonomy
      )
    })
    .flatMap(p => {
      return ['en', ...supportedLanguages].map(lang => ({
        params: {
          lang,
          slug: p.url.artist,
          collectionTaxonomy: p.url.collectionTaxonomy,
          collection: p.url.collection,
          artworkSlug: p.url.slug,
        },
      }))
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionPDP
